import {Component, OnInit, OnDestroy, AfterViewInit, signal, inject, ViewChild} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ActivatedRoute, Router, RouterModule} from '@angular/router';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {
  faBowlFood,
  faClock,
  faFire,
  faUsers,
  faLock,
  faHouse,
  faGlobe,
  faThermometerHalf,
  faStopwatch,
  faEllipsisH,
  faBook,
  faCheckCircle,
  faTimesCircle,
  faQuestionCircle,
  faShoppingCart,
  faPen,
  faLink,
  faRobot,
  faUser,
  faUtensils,
  faCartPlus,
  faCircleCheck,
  faCircleXmark,
  faCirclePlus,
  faCarrot,
  faImage,
  faCamera,
  faMagnifyingGlass,
  faRotateLeft
} from '@fortawesome/free-solid-svg-icons';
import {RecipeDetailDto, RecipeSourceDto, RecipeStepDto, RecipeService} from '../../services/recipe/recipe.service';
import {RecipeImage, RecipeImageService} from '../../services/recipe/recipe-image.service';
import {RecipeImageDialogComponent} from '../../modals/recipe-image-dialog/recipe-image-dialog.component';
import {RecipeFacetsService} from '../../services/recipe/recipe-facets.service';
import {RecipeFacetBadgesComponent} from '../../components/recipe-facets/recipe-facet-badges.component';
import {StockService, IngredientMatchResponse, IngredientMatchStatus} from '../../services/stock/stock.service';
import {ShoppingService} from '../../services/shopping/shopping.service';
import {ContextService} from '../../services/context/context-signal.service';
import {PageHeaderService} from '../../services/page-header/page-header.service';
import {PageAction} from '../../components/app-shell/page-action.model';
import {
  UiBadgeComponent,
  UiButtonComponent,
  UiCardComponent,
  UiModalComponent,
  UiTooltipDirective,
  UiStepsComponent,
  UiStepItemDirective,
  UiToastService
} from '../../shared/ui';
import {RecipeRatingsComponent} from '../../components/recipe-rating/recipe-ratings.component';
import {Observable, of, Subject, Subscription} from 'rxjs';
import {catchError, map, debounceTime, switchMap} from 'rxjs/operators';
import {AppLogger, AppLoggerService} from '../../core/logging';
import {provideViewContext} from '../../services/view-context/provide-view-context';
import {ConfirmService} from '../../services/confirm/confirm.service';

// View model for ingredient rendering + stock matching
type RecipeIngredientVM = {
  key: string;
  name: string;
  quantityReadable?: string;
  unit?: string;
  amount?: number;
};

@Component({
  selector: 'app-recipe',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FontAwesomeModule,
    UiBadgeComponent,
    UiButtonComponent,
    UiCardComponent,
    UiModalComponent,
    UiTooltipDirective,
    UiStepsComponent,
    UiStepItemDirective,
    RecipeRatingsComponent,
    RecipeFacetBadgesComponent,
    RecipeImageDialogComponent
  ],
  templateUrl: './recipe.component.html',
  styleUrl: './recipe.component.scss'
})
export class RecipeComponent implements OnInit, AfterViewInit, OnDestroy {
  // Icons
  faBowlFood = faBowlFood;
  faClock = faClock;
  faFire = faFire;
  faUsers = faUsers;
  faLock = faLock;
  faHouse = faHouse;
  faGlobe = faGlobe;
  faThermometerHalf = faThermometerHalf;
  faStopwatch = faStopwatch;
  faEllipsisH = faEllipsisH;
  faBook = faBook;
  faCheckCircle = faCheckCircle;
  faTimesCircle = faTimesCircle;
  faQuestionCircle = faQuestionCircle;
  faShoppingCart = faShoppingCart;
  faPen = faPen;
  faLink = faLink;
  faRobot = faRobot;
  faUser = faUser;
  faUtensils = faUtensils;
  faCartPlus = faCartPlus;
  faCircleCheck = faCircleCheck;
  faCircleXmark = faCircleXmark;
  faCirclePlus = faCirclePlus;
  faCarrot = faCarrot;
  faImage = faImage;
  faCamera = faCamera;
  faSearch = faMagnifyingGlass;
  faRotateLeft = faRotateLeft;

  recipe = signal<RecipeDetailDto | null>(null);
  // ── Portion scaling ───────────────────────────────────────────────────────
  // The user can rescale the recipe to a different portion count. The backend
  // recomputes every quantity (view-only, nothing is saved). `selectedServings`
  // is the user's pick (null = the recipe's own base size); the count updates
  // optimistically while a debounced request fetches the rescaled recipe.
  selectedServings = signal<number | null>(null);
  isRescaling = signal<boolean>(false);
  private recipeId: number | null = null;
  private readonly maxServings = 99;
  private readonly rescale$ = new Subject<number>();
  private rescaleSub?: Subscription;
  /** URL of the recipe's title image (primary `full` variant), or null when the
   *  recipe has no images. Loaded separately from the recipe detail. */
  heroImageUrl = signal<string | null>(null);
  isLoading = signal<boolean>(false);
  errorMessage = signal<string | null>(null);
  ingredientStockMatches = signal<Map<string, IngredientMatchResponse>>(new Map());
  isLoadingStockStatus = signal<boolean>(false);
  collapsedIngredients = signal<Set<string>>(new Set());
  imageErrors = signal<Set<number>>(new Set()); // Track product IDs with image errors
  dishTypeLabels = signal<Map<string, string>>(new Map());
  // Guards the "Save as meal" action against double-taps while the POST is in flight.
  isCreatingMeal = signal<boolean>(false);

  // ── Add to shopping cart ──────────────────────────────────────────────────
  // The "Add to shopping cart" button opens a modal with a shopping-cart preview
  // that lets the user review and trim the buy list before committing it. The
  // narrow sidebar can't hold a grid for many items, so the review lives in a
  // wide modal. Mirrors the iOS RecipeIngredientsView sheet.
  @ViewChild('cartPreviewModal') cartPreviewModal?: UiModalComponent;

  // Image management lives in a modal opened from the hero image, mirroring the
  // product detail page (and the iOS handling). Only editable recipes open it.
  @ViewChild(RecipeImageDialogComponent) imageDialog?: RecipeImageDialogComponent;
  // Ingredient keys already added to the cart (drives the per-row checkmark).
  addedToCart = signal<Set<string>>(new Set());
  // Preview cards the user has deselected; everything else is included.
  excludedFromCart = signal<Set<string>>(new Set());

  private contextService = inject(ContextService);
  private stockService = inject(StockService);
  private recipeImageService = inject(RecipeImageService);
  private shoppingService = inject(ShoppingService);
  // Active shopping list, resolved lazily on the first add and cached for the
  // rest of the visit (mirrors the iOS view model).
  private cachedListId: number | null = null;
  private recipeFacetsService = inject(RecipeFacetsService);
  private pageHeaderService = inject(PageHeaderService);
  private confirm = inject(ConfirmService);
  private toast = inject(UiToastService);

  private readonly log: AppLogger;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private recipeService: RecipeService,
    private sanitizer: DomSanitizer,
    private appLogger: AppLoggerService,
  ) {
    this.log = this.appLogger.createForClass(RecipeComponent);

    provideViewContext('recipe_detail', () => {
      const r = this.recipe();
      return r ? [{type: 'recipe', id: r.id, label: r.title ?? ''}] : [];
    });
  }

  ngOnInit(): void {
    // Push the back button into the global sub-header. Prefer in-app history so
    // we return to wherever the recipe was opened from (recipe list, meal
    // planner, …); fall back to the recipe list on deep-link / refresh.
    this.pageHeaderService.setBackButton({
      label: 'Back',
      preferHistoryBack: true,
      fallbackUrl: '/recipes',
      display: 'auto'
    });

    // Resolve dish-type KEYS (the detail facets carry bare keys) to labels.
    this.recipeFacetsService.getCatalogue().subscribe({
      next: (cat) => {
        const map = new Map<string, string>();
        for (const c of cat.dish_types ?? []) {
          map.set(c.key, c.name_de || c.name_en || c.key);
        }
        this.dishTypeLabels.set(map);
      },
      error: (err) => this.log.error('Error loading recipe facet catalogue', err),
    });

    // Debounced portion rescaling. Rapid +/- taps collapse into a single
    // request; switchMap cancels any in-flight fetch so only the latest wins.
    this.rescaleSub = this.rescale$.pipe(
      debounceTime(250),
      switchMap((target) => {
        const id = this.recipeId;
        if (id == null) return of(null);
        // Requesting the base size returns the unscaled recipe (no query param).
        const req = target === this.baseServings() ? undefined : target;
        return this.recipeService.getRecipeById(id, req).pipe(
          catchError((err) => {
            this.log.error('Error rescaling recipe portions', err, {id, target});
            this.toast.error('Could not rescale portions.');
            return of(null);
          })
        );
      })
    ).subscribe((dto) => {
      this.isRescaling.set(false);
      if (dto) {
        // Only the recipe body changes; hero image and stock matches (keyed by
        // ingredient, unaffected by scaling) are intentionally left in place.
        this.recipe.set(dto);
      }
    });

    this.load();
  }

  ngAfterViewInit(): void {
    // Initial publish; the Edit action only appears once the recipe is loaded
    // and the user has write permission (see load() → refreshPageActions()).
    this.refreshPageActions();
  }

  /**
   * Declarative toolbar actions.
   *  - Meal: turns the recipe's leftovers into a product. State-aware — "View
   *    meal" (navigate) once one exists, otherwise "Save as meal" (create then
   *    navigate). Available to anyone who can view the recipe (the product
   *    lands in the viewer's own household).
   *  - Edit: secondary action button. Inline on desktop, auto-collapses into
   *    the ⋯ overflow on mobile. Only published when the user may edit.
   */
  private refreshPageActions(): void {
    const r = this.recipe();
    if (!r) {
      this.pageHeaderService.setPageActions(null);
      return;
    }

    const hasMeal = r.meal_product_id != null;

    const actions: PageAction[] = [
      {
        type: 'button',
        id: 'meal',
        label: hasMeal ? 'View meal' : 'Save as meal',
        icon: this.faUtensils,
        variant: 'secondary',
        disabled: this.isCreatingMeal(),
        onClick: () => this.saveOrOpenMeal(),
      },
    ];

    if (this.canEditRecipe(r)) {
      actions.push({
        type: 'button',
        id: 'edit',
        label: 'Edit',
        icon: this.faPen,
        variant: 'secondary',
        onClick: () => this.router.navigate(['/recipe', r.id, 'edit']),
      });
    }

    this.pageHeaderService.setPageActions(actions);
  }

  /**
   * If the recipe already has a meal product, navigate straight to it.
   * Otherwise ask for confirmation — creating a meal is a side effect and we
   * don't want "ghost" meals from users just clicking to see what happens —
   * then create it (idempotent server-side) and navigate.
   */
  async saveOrOpenMeal(): Promise<void> {
    const r = this.recipe();
    if (!r) return;

    if (r.meal_product_id != null) {
      this.router.navigate(['/products', r.meal_product_id]);
      return;
    }

    if (this.isCreatingMeal()) return;

    const ok = await this.confirm.ask({
      title: 'Save as meal',
      message: `This creates a meal product from "${r.title ?? 'this recipe'}" in your household. Continue?`,
      confirmText: 'Save as meal',
      cancelText: 'Cancel',
      confirmBtnClass: 'btn-primary',
      modalOptions: {size: 'md'},
    });
    if (!ok) return;

    this.isCreatingMeal.set(true);
    this.refreshPageActions();

    this.recipeService.createMeal(r.id).subscribe({
      next: (resp) => {
        this.isCreatingMeal.set(false);
        // Reflect the new link locally so the action flips to "View meal".
        this.recipe.set({...r, meal_product_id: resp.product_id});
        this.router.navigate(['/products', resp.product_id]);
      },
      error: (err) => {
        this.isCreatingMeal.set(false);
        this.refreshPageActions();
        this.errorMessage.set('Could not create a meal from this recipe.');
        this.log.error('Error creating meal from recipe', err, {id: r.id});
      }
    });
  }

  ngOnDestroy(): void {
    // Clear all page header content when leaving the page
    this.pageHeaderService.clearAll();
    this.rescaleSub?.unsubscribe();
  }

  private load(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const id = idParam ? Number(idParam) : NaN;

    if (!Number.isFinite(id)) {
      this.errorMessage.set('Invalid recipe id.');
      return;
    }

    this.recipeId = id;
    // Restore a scaled portion size from the URL so a hard refresh / shared link
    // reopens at the chosen size. Invalid values are ignored (and cleaned up
    // once the base size is known).
    const requestedServings = this.parseServingsParam(this.route.snapshot.queryParamMap.get('servings'));
    this.selectedServings.set(requestedServings);
    this.isRescaling.set(false);

    this.isLoading.set(true);
    this.errorMessage.set(null);

    this.recipeService.getRecipeById(id, requestedServings ?? undefined).subscribe({
      next: (dto) => {
        this.recipe.set(dto);
        this.isLoading.set(false);

        // Normalize the URL: drop ?servings when it's absent-worthy — i.e. the
        // requested size equals the base, or the param was junk.
        const base = dto.base_servings ?? dto.servings ?? 0;
        if (base > 0 && requestedServings === base) {
          this.selectedServings.set(null);
          this.syncServingsToUrl(null);
        } else if (requestedServings == null && this.route.snapshot.queryParamMap.has('servings')) {
          this.syncServingsToUrl(null);
        }

        // Set page title
        this.pageHeaderService.setPageTitle(dto.title || 'Recipe');

        // Publish Edit action now that we know the permission.
        this.refreshPageActions();

        this.loadIngredientStockMatches(dto);
        // Hero image comes straight from the detail response (full, else thumb).
        this.heroImageUrl.set(dto.image_url ?? dto.thumbnail_url ?? null);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.errorMessage.set('Could not load recipe.');
        this.log.error('Error loading recipe', err, {id});
      }
    });
  }

  // ── Recipe images ─────────────────────────────────────────────────────────

  /** Source URL for the image manager (drives "refetch from source"); empty
   *  unless the recipe was imported from a URL. */
  imageSourceUrl(): string {
    const r = this.recipe();
    return r?.source?.type === 'import' ? (r.source.url ?? '') : '';
  }

  /** Whether the current user may manage this recipe's images (edit rights). */
  canManageImages(): boolean {
    const r = this.recipe();
    return !!r && this.canEditRecipe(r);
  }

  /** Open the image management modal from the hero image / placeholder. */
  onImageClick(): void {
    if (!this.canManageImages()) return;
    this.imageDialog?.open();
  }

  /** Open image management straight on the search panel. */
  onImageSearchClick(): void {
    if (!this.canManageImages()) return;
    this.imageDialog?.open(true);
  }

  /**
   * The image modal was dismissed — the gallery may have changed, so refresh
   * the hero from the current primary image without a full recipe reload.
   */
  onImageDialogClosed(): void {
    const r = this.recipe();
    if (!r) return;
    this.recipeImageService.getImages(r.id).subscribe({
      next: (all: RecipeImage[]) => {
        const full = all.filter(img => img.type === 'full');
        const hero = full.find(img => img.is_primary) ?? full[0];
        this.heroImageUrl.set(hero?.url ?? null);
      },
      error: (err) => this.log.error('Failed to refresh recipe hero image', err, {id: r.id})
    });
  }

  // ── Portion scaling ─────────────────────────────────────────────────────────

  /** The portion count the recipe was authored for (0 when unknown). */
  baseServings(): number {
    const r = this.recipe();
    return r?.base_servings ?? r?.servings ?? 0;
  }

  /** The portion count currently displayed (user's pick, else the base). */
  currentServings(): number {
    return this.selectedServings() ?? this.baseServings();
  }

  /** Scaling is only offered when the recipe has a positive base size. */
  canScalePortions(): boolean {
    return this.baseServings() > 0;
  }

  /** True while showing a size other than the authored one. */
  isPortionsScaled(): boolean {
    return this.canScalePortions() && this.currentServings() !== this.baseServings();
  }

  /** Nudge the portion count by ±1 (bounds enforced in setPortions). */
  changePortions(delta: number): void {
    this.setPortions(this.currentServings() + delta);
  }

  /** Set the portion count, clamp it, and kick off a debounced rescale. */
  setPortions(n: number): void {
    if (!this.canScalePortions()) return;
    const clamped = Math.max(1, Math.min(this.maxServings, Math.round(n)));
    if (clamped === this.currentServings()) return;
    this.selectedServings.set(clamped); // optimistic count; amounts follow on fetch
    this.isRescaling.set(true);
    // Reflect the choice in the URL so a refresh / shared link keeps it. The base
    // size carries no param (keeps canonical recipe URLs clean).
    this.syncServingsToUrl(clamped === this.baseServings() ? null : clamped);
    this.rescale$.next(clamped);
  }

  /** Return to the recipe's authored portion size. */
  resetPortions(): void {
    this.setPortions(this.baseServings());
  }

  /** Parse a `servings` query value into a clamped positive int, or null. */
  private parseServingsParam(raw: string | null): number | null {
    if (raw == null) return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.max(1, Math.min(this.maxServings, Math.round(n)));
  }

  /** Write (or clear, when null) the `servings` query param without reloading
   *  the component — history is replaced so stepping doesn't spam the back stack. */
  private syncServingsToUrl(servings: number | null): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {servings: servings ?? null},
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /**
   * Ingredients to render in the sidebar.
   * Prefer the new `recipe.ingredients` objects; fallback to legacy `aggregated_ingredients`.
   */
  ingredientVMs(): RecipeIngredientVM[] {
    const r = this.recipe();
    if (!r) return [];

    if (r.ingredients && r.ingredients.length > 0) {
      return r.ingredients
        .filter(i => i && i.is_active !== false)
        .map(i => {
          const name = (i.display_name || i.canonical || i.key || '').trim();

          // New backend provides an optional per-ingredient aggregate (may be null)
          const q = i.aggregate?.quantity ?? null;

          const quantityReadable = (typeof q?.readable === 'string' && q.readable.trim().length > 0)
            ? q.readable
            : undefined;

          // Only forward numeric amount/unit when they are usable for stock matching.
          const amount = (typeof q?.amount === 'number' && Number.isFinite(q.amount)) ? q.amount : undefined;
          const unit = (amount !== undefined && typeof q?.unit === 'string' && q.unit.trim().length > 0)
            ? q.unit
            : undefined;

          const key = String(i.id ?? i.key ?? name).toLowerCase();

          return {
            key,
            name: name || '(unknown)',
            quantityReadable,
            unit,
            amount
          };
        })
        .filter(vm => vm.name !== '(unknown)');
    }

    return (r.aggregated_ingredients ?? []).map(a => ({
      key: a.name,
      name: a.name,
      quantityReadable: a.quantity?.readable,
      unit: a.quantity?.unit,
      amount: a.quantity?.amount
    }));
  }

  private loadIngredientStockMatches(recipe: RecipeDetailDto): void {
    const vms: RecipeIngredientVM[] = (recipe.ingredients && recipe.ingredients.length > 0)
      ? this.ingredientVMs()
      : (recipe.aggregated_ingredients ?? []).map(a => ({
        key: a.name,
        name: a.name,
        quantityReadable: a.quantity?.readable,
        unit: a.quantity?.unit,
        amount: a.quantity?.amount
      }));

    if (!vms || vms.length === 0) {
      return;
    }

    const household = this.contextService.context()?.household;

    // If household is not yet loaded, wait a bit and retry
    if (!household?.id) {
      this.log.debug('Household context not yet loaded, retrying', {retryInMs: 500});
      setTimeout(() => this.loadIngredientStockMatches(recipe), 500);
      return;
    }

    this.isLoadingStockStatus.set(true);

    // Counter to track when all requests complete
    let completedRequests = 0;
    const totalRequests = vms.length;

    // Clear previous matches when reloading
    this.ingredientStockMatches.set(new Map());

    // Load each ingredient individually and display as soon as ready
    vms.forEach(vm => {
      this.stockService.getIngredientStockMatch(
        household.id!,
        vm.name,
        vm.quantityReadable
      ).pipe(
        catchError(err => {
          this.log.error('Error checking ingredient stock', err, {
            ingredientName: vm.name,
            ingredientKey: vm.key,
            quantity: vm.quantityReadable
          });
          return of(null);
        })
      ).subscribe(result => {
        if (result) {
          // Update the map immediately for this ingredient
          const currentMap = this.ingredientStockMatches();
          const newMap = new Map(currentMap);
          newMap.set(vm.key, result);
          this.ingredientStockMatches.set(newMap);
        }

        // Track completion
        completedRequests++;
        if (completedRequests === totalRequests) {
          this.isLoadingStockStatus.set(false);
        }
      });
    });
  }

  /**
   * The rating endpoint reported a 403 — access was revoked mid-session.
   * Don't retry; send the user back to the (refetched) recipe list.
   */
  onRatingAccessRevoked(): void {
    this.router.navigate(['/recipes']);
  }

  sourceLabel(source?: RecipeSourceDto): string {
    // Show the origin site (e.g. "chefkoch.de") for imported recipes, falling
    // back to "Imported" when the URL is missing or unparseable.
    if (source?.type === 'import') return this.sourceDomain(source.url) ?? 'Imported';
    if (source?.type === 'assistant') return 'By Assistant';
    // Hand-authored recipes: show who created it, falling back to "Created"
    // when the backend didn't enrich the creator's name.
    return this.authorName() ?? 'Created';
  }

  /** The creator's display name, when the detail response carries it. */
  authorName(): string | null {
    const name = this.recipe()?.created_by?.user_name?.trim();
    return name ? name : null;
  }

  /** Bare host of a source URL without a leading "www." (null if unparseable). */
  private sourceDomain(url?: string): string | null {
    if (!url) return null;
    try {
      return new URL(url).hostname.replace(/^www\./, '') || null;
    } catch {
      return null;
    }
  }

  sourceIcon(source?: RecipeSourceDto) {
    if (source?.type === 'import') return this.faLink;
    if (source?.type === 'assistant') return this.faRobot;
    return this.faUser;
  }

  stepIcon(step: RecipeStepDto) {
    if (step.type === 'cook') return this.faFire;
    if (step.type === 'wait') return this.faClock;
    if (step.type === 'instruction') return this.faBook;
    return this.faBowlFood;
  }

  /**
   * Get the color variant for timeline based on step type
   */
  stepTimelineColor(step: RecipeStepDto): 'primary' | 'error' | 'success' | 'accent' {
    if (step.type === 'cook') return 'error'; // red
    if (step.type === 'wait') return 'success'; // green
    if (step.type === 'serve') return 'accent'; // purple/accent
    return 'primary'; // instruction - blue/primary
  }

  /**
   * Determine timeline position based on step type
   * instruction and serve go left, cook and wait go right
   */
  stepTimelinePosition(step: RecipeStepDto): 'start' | 'end' {
    if (step.type === 'instruction' || step.type === 'serve') {
      return 'start'; // left side
    }
    return 'end'; // right side (cook, wait)
  }

  cookSummary(step: RecipeStepDto): string | null {
    if (step.type !== 'cook') return null;

    const parts: string[] = [];

    if (step.device) {
      parts.push(step.device);
    }

    if (step.device === 'oven' && step.mode) {
      parts.push(step.mode);
    }

    if (step.device === 'pan' || step.device === 'grill') {
      if (typeof step.heat_level === 'number') parts.push(`heat ${step.heat_level}/9`);
    } else {
      if (typeof step.temp_c === 'number') parts.push(`${step.temp_c}°C`);
    }

    if (step.device === 'grill' && step.grill_mode) {
      parts.push(step.grill_mode === 'indirect' ? 'indirect heat' : 'direct heat');
    }

    if (typeof step.time_min === 'number') parts.push(`${step.time_min} min`);

    if (step.device === 'oven') {
      if (step.preheat === true) parts.push('preheat');
      if (typeof step.rack_position === 'number') parts.push(`rack ${step.rack_position}`);
    }

    return parts.length ? parts.join(' • ') : null;
  }

  waitSummary(step: RecipeStepDto): string | null {
    if (step.type !== 'wait') return null;

    const parts: string[] = [];
    if (typeof step.time_min === 'number') parts.push(`${step.time_min} min`);
    if (step.location) parts.push(step.location);
    if (step.covered === true) parts.push('covered');

    return parts.length ? parts.join(' • ') : null;
  }

  /**
   * Rendered step text.
   * Backend always provides text_html; we sanitize it and only then trust the result.
   */
  stepTextHtml(step: RecipeStepDto): SafeHtml {
    const html = step.text_html ?? '';

    // Let Angular sanitize first (removes scripts, unsafe URLs, etc.)
    // Use '1' as the HTML security context to avoid version-specific SecurityContext imports.
    const sanitized = this.sanitizer.sanitize(1 as any, html) as string | null;

    // If sanitizer removes everything, return empty content (no legacy fallback).
    return this.sanitizer.bypassSecurityTrustHtml(sanitized ?? '');
  }

  /**
   * Whether the current user is allowed to edit this recipe. The backend
   * reports this directly (true for members of the owning household).
   */
  canEditRecipe(recipe: RecipeDetailDto): boolean {
    return recipe?.can_edit === true;
  }

  /**
   * Get the stock match status for a specific ingredient
   */
  getIngredientStockStatus(ingredientKey: string): IngredientMatchStatus | null {
    return this.ingredientStockMatches().get(ingredientKey)?.status ?? null;
  }

  /**
   * Get the icon for a given stock status
   */
  getStockStatusIcon(status: IngredientMatchStatus | null) {
    if (!status) return null;

    switch (status) {
      case 'available':
        return this.faCheckCircle;
      case 'insufficient':
        return this.faTimesCircle;
      case 'uncertain':
        return this.faQuestionCircle;
      case 'not_found':
        return this.faShoppingCart;
      default:
        return null;
    }
  }

  /**
   * Get the color class for a given stock status
   */
  getStockStatusColor(status: IngredientMatchStatus | null): string {
    if (!status) return '';

    switch (status) {
      case 'available':
        return 'text-success'; // green
      case 'insufficient':
        return 'text-danger'; // red
      case 'uncertain':
        return 'text-secondary'; // grey
      case 'not_found':
        return 'text-purple'; // violet/purple
      default:
        return '';
    }
  }

  /**
   * Get the tooltip text for a given stock status
   */
  getStockStatusTooltip(status: IngredientMatchStatus | null): string {
    if (!status) return '';

    switch (status) {
      case 'available':
        return 'In Stock';
      case 'insufficient':
        return 'Out of Stock';
      case 'uncertain':
        return 'We could not uniquely identify a product';
      case 'not_found':
        return 'Not in Assortment, please buy';
      default:
        return '';
    }
  }

  /**
   * Check if an ingredient is collapsed
   */
  isIngredientCollapsed(ingredientKey: string): boolean {
    return !this.collapsedIngredients().has(ingredientKey);
  }

  /**
   * Toggle collapse state for an ingredient
   */
  toggleIngredientCollapse(ingredientKey: string): void {
    const current = this.collapsedIngredients();
    const newSet = new Set(current);

    if (newSet.has(ingredientKey)) {
      newSet.delete(ingredientKey);
    } else {
      newSet.add(ingredientKey);
    }

    this.collapsedIngredients.set(newSet);
  }

  /**
   * Get matches for a specific ingredient
   */
  getIngredientMatches(ingredientKey: string): IngredientMatchResponse | null {
    return this.ingredientStockMatches().get(ingredientKey) ?? null;
  }

  /**
   * Handle image load errors
   */
  onImageError(productId: number): void {
    const current = this.imageErrors();
    const newSet = new Set(current);
    newSet.add(productId);
    this.imageErrors.set(newSet);
  }

  /**
   * Check if an image has failed to load
   */
  isImageError(productId: number): boolean {
    return this.imageErrors().has(productId);
  }

  availabilityBadgeVariant(match: IngredientMatchResponse['matches'][number]): UiBadgeComponent['variant'] {
    const s = match.stock_available?.status;
    switch (s) {
      case 'enough':
        return 'success';
      case 'available':
        return 'success';
      case 'not_enough':
        return 'warning';
      case 'unknown':
        return 'neutral';
      case 'out_of_stock':
        return 'error';
      default:
        return 'neutral';
    }
  }

  availabilityBadgeText(match: IngredientMatchResponse['matches'][number]): string {
    const info = match.stock_available;
    if (!info) return '';

    const n = info.instances_total;
    const suffix = n === 1 ? 'left' : 'left';

    // Prefer explicit availability first
    if (info.status === 'out_of_stock') return '0 left';
    if (Number.isFinite(n)) return `${n} ${suffix}`;

    return info.is_available ? 'Available' : 'Out of stock';
  }

  matchTypeLabel(matchType?: string): string {
    return (matchType ?? '').trim() || '\u2014';
  }

  // \u2500\u2500 Add to shopping cart \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500

  /** Whether an ingredient has already been added to the cart this session. */
  isAddedToCart(key: string): boolean {
    return this.addedToCart().has(key);
  }

  /**
   * Add a single ingredient to the shopping cart from its row. Routes through
   * {@link commitAddToCart} so there's one integration point. `$event` is
   * stopped so the click doesn't also toggle the row's collapse state.
   */
  addIngredientToCart(vm: RecipeIngredientVM, event?: Event): void {
    event?.stopPropagation();
    if (this.isAddedToCart(vm.key)) return;
    this.commitAddToCart([vm]);
  }

  /**
   * Open the cart-preview modal. Buy candidates (not in stock) start selected;
   * in-stock items are listed too but start deselected, since the user usually
   * doesn't need to re-buy what they already have — they can opt in per item.
   */
  openCartPreview(): void {
    const excluded = new Set<string>();
    for (const vm of this.cartPreviewItems()) {
      if (this.getIngredientStockStatus(vm.key) === 'available') {
        excluded.add(vm.key);
      }
    }
    this.excludedFromCart.set(excluded);
    this.cartPreviewModal?.open();
  }

  /** Close the cart-preview modal. */
  closeCartPreview(): void {
    this.cartPreviewModal?.close();
  }

  /**
   * Items for the preview: every active ingredient with a resolved match that
   * hasn't been added yet. Uncertain matches \u2014 the ones the user most likely
   * wants to drop \u2014 sort first, then not_found, then insufficient, and finally
   * the in-stock ones (listed but deselected by default). Mirrors the iOS
   * ordering.
   */
  cartPreviewItems(): RecipeIngredientVM[] {
    const order: Record<string, number> = {uncertain: 0, not_found: 1, insufficient: 2, available: 3};
    const added = this.addedToCart();
    return this.ingredientVMs()
      .filter(vm => {
        if (added.has(vm.key)) return false;
        const status = this.getIngredientStockStatus(vm.key);
        return status != null && status in order;
      })
      .sort((a, b) => {
        const sa = order[this.getIngredientStockStatus(a.key) as string] ?? 9;
        const sb = order[this.getIngredientStockStatus(b.key) as string] ?? 9;
        return sa !== sb ? sa - sb : a.name.localeCompare(b.name);
      });
  }

  /** True when the preview card is selected (not deselected) for adding. */
  isCartSelected(key: string): boolean {
    return !this.excludedFromCart().has(key);
  }

  /** Toggle whether a preview card will be added. */
  toggleCartSelection(key: string): void {
    const next = new Set(this.excludedFromCart());
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    this.excludedFromCart.set(next);
  }

  /** Number of preview cards currently selected for adding. */
  selectedCartCount(): number {
    return this.cartPreviewItems().filter(vm => this.isCartSelected(vm.key)).length;
  }

  /** Add every selected preview item to the cart, then close the modal. */
  addSelectedToCart(): void {
    const selected = this.cartPreviewItems().filter(vm => this.isCartSelected(vm.key));
    if (selected.length === 0) return;
    this.commitAddToCart(selected);
    this.cartPreviewModal?.close();
  }

  /**
   * Single funnel for adding ingredients to the shopping cart.
   *
   * Adds go in as raw input ("<quantity> <name>"), which the backend now
   * accepts immediately and parses in a background task \u2014 so we fire all of the
   * selected items concurrently and reflect them optimistically. Each item that
   * fails is rolled back individually with an error toast.
   */
  private commitAddToCart(items: RecipeIngredientVM[]): void {
    if (items.length === 0) return;

    const householdId = this.contextService.context()?.household?.id;
    if (!householdId) {
      this.toast.error('Kein Haushalt geladen.');
      return;
    }

    // Optimistically reflect the add (drives the per-row checkmark), then
    // resolve the list and fire the real requests.
    const next = new Set(this.addedToCart());
    for (const vm of items) next.add(vm.key);
    this.addedToCart.set(next);

    const msg = items.length === 1
      ? `\u201e${items[0].name}" zum Warenkorb hinzugef\u00fcgt`
      : `${items.length} Zutaten zum Warenkorb hinzugef\u00fcgt`;
    this.toast.success(msg);

    this.resolveActiveListId(householdId).subscribe({
      next: (listId) => {
        if (listId == null) {
          this.revertAddedToCart(items);
          this.toast.error('Keine Einkaufsliste vorhanden.');
          return;
        }
        for (const vm of items) {
          const rawInput = vm.quantityReadable
            ? `${vm.quantityReadable} ${vm.name}`
            : vm.name;
          this.shoppingService.addItem(householdId, listId, {
            client_id: crypto.randomUUID(),
            raw_input: rawInput,
          }).subscribe({
            error: (err) => {
              this.log.error('Failed to add ingredient to shopping list', err, {ingredient: vm.name});
              this.revertAddedToCart([vm]);
              this.toast.error(`\u201e${vm.name}" konnte nicht hinzugef\u00fcgt werden.`);
            },
          });
        }
      },
      error: (err) => {
        this.log.error('Failed to resolve active shopping list', err);
        this.revertAddedToCart(items);
        this.toast.error('Einkaufsliste konnte nicht geladen werden.');
      },
    });
  }

  /**
   * Resolves the household's active shopping list id (or its first list),
   * caching the result for subsequent adds during the visit.
   */
  private resolveActiveListId(householdId: number): Observable<number | null> {
    if (this.cachedListId != null) return of(this.cachedListId);
    return this.shoppingService.getLists(householdId).pipe(
      map(lists => {
        const active = lists.find(l => l.is_active) ?? lists[0];
        this.cachedListId = active?.id ?? null;
        return this.cachedListId;
      }),
    );
  }

  /** Rolls back the optimistic "added" state for the given ingredients. */
  private revertAddedToCart(items: RecipeIngredientVM[]): void {
    const next = new Set(this.addedToCart());
    for (const vm of items) next.delete(vm.key);
    this.addedToCart.set(next);
  }
}
